/**
 * Synthetic per-variant performance card NODE.
 *
 * The React Flow renderer for the rendered-only perf-card nodes that
 * `injectPerfCardNodes` places at the head of each VARIANT_SPLIT variant lane.
 * ELK lays the node out (centered in the lane, lanes equalized), so the card —
 * and the variant name in its header — sits directly under the lane's 90° turn,
 * and the edges into/out of the card carry the ordinary on-edge add buttons.
 *
 * The whole card is clickable → opens the Results drawer. Metrics are resolved
 * from the shared SplitTestResultsContext (fetched once at the builder level),
 * and the single leader is decided centrally (keyed by variant NAME via
 * isLeaderRow, so exactly one card can ever lead).
 */
import React, { useMemo } from 'react'
import { Handle, NodeProps, Position } from '@xyflow/react'
import { JourneyNode } from '../../types/journey-nodes'
import { useSplitTestResultsContext } from '../../split-test-results/split-test-results-context'
import {
    findRowForBranch,
    getRowsForSplit,
    isLeaderRow,
    shouldShowPurchaseColumns,
} from '../../split-test-results/split-test-metrics'
import { VariantPerformanceCard } from '@pushly/aqe/lib/components'
import { PERF_CARD_WIDTH, PERF_CARD_HEIGHT } from '../../constants'

export const PerfCardNode = ({ data, targetPosition, sourcePosition }: NodeProps<JourneyNode>) => {
    const splitResults = useSplitTestResultsContext()
    const meta = data.uiMeta?.perfCardOf

    const info = useMemo(() => {
        if (!meta) return null
        // Scope to this card's own split test (by step id), then resolve the
        // variant within it — names are unique within a split, so this can't pick
        // up another split's same-named variant.
        const splitRows = getRowsForSplit(meta.splitId, splitResults.rows)
        const row = findRowForBranch(splitRows, { key: meta.branchKey, label: meta.variantName })
        // Leader is decided PER split test — look up this card's own split's cue.
        const cue = splitResults.leaderCueBySplitId[String(meta.splitId)] ?? null
        const isLeader = isLeaderRow(cue, row)
        return {
            row,
            isLeader,
            leaderMetric: isLeader ? cue!.metric : null,
            showPurchaseRate: shouldShowPurchaseColumns(splitRows),
        }
    }, [meta, splitResults.rows, splitResults.leaderCueBySplitId])

    if (!meta || !info) return null

    return (
        <div
            className="perf-card-node no-drag"
            role="button"
            tabIndex={0}
            aria-label={`View split test results for ${meta.variantName}`}
            style={{ width: PERF_CARD_WIDTH, height: PERF_CARD_HEIGHT, position: 'relative' }}
            onClick={(e) => {
                e.stopPropagation()
                splitResults.openResultsPanel(meta.splitId)
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    splitResults.openResultsPanel(meta.splitId)
                }
            }}
        >
            <Handle
                type="target"
                position={targetPosition ?? Position.Top}
                style={{ opacity: 0, width: 0, height: 0, top: 0 }}
            />
            <VariantPerformanceCard
                variantName={meta.variantName}
                percentage={meta.percentage ?? null}
                variant={info.row}
                isLeader={info.isLeader}
                leaderMetric={info.leaderMetric}
                showPurchaseRate={info.showPurchaseRate}
                loading={splitResults.isLoading}
                empty={splitResults.isDraft}
            />
            <Handle
                type="source"
                position={sourcePosition ?? Position.Bottom}
                style={{ opacity: 0, width: 0, height: 0, bottom: 0 }}
            />
        </div>
    )
}
